/**
 * Supabase session management utilities
 */

import type { Session } from '@supabase/supabase-js';
import { AUTH_CONSTANTS } from './constants';

/**
 * Result type for session retrieval operations
 */
export interface SessionResult {
  data: { session: Session | null };
  error: Error | null;
}

/**
 * Retrieves Supabase session with a timeout to prevent hanging requests.
 * Uses Promise.race to ensure the operation completes within the specified timeout.
 *
 * @param timeoutMs - Timeout in milliseconds (defaults to SESSION_CHECK_TIMEOUT_MS)
 * @returns Promise resolving to SessionResult with session data or error
 *
 * @example
 * ```typescript
 * const result = await getSessionWithTimeout(2000);
 * if (result.data.session?.access_token) {
 *   // Use the session
 * }
 * ```
 */
export async function getSessionWithTimeout(
  timeoutMs: number = AUTH_CONSTANTS.SESSION_CHECK_TIMEOUT_MS
): Promise<SessionResult> {
  try {
    const { supabase } = await import('../supabase');
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<SessionResult>((resolve) =>
      setTimeout(() => resolve({ data: { session: null }, error: null }), timeoutMs)
    );

    const result = await Promise.race([sessionPromise, timeoutPromise]);

    // Safely extract session and error from result
    return {
      data: { session: result?.data?.session ?? null },
      error: result?.error ?? null,
    };
  } catch (error) {
    return {
      data: { session: null },
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}
